import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { isDeterministicClientError } from '../lib/query/queryClient';
import { isTerminalJobStatus, type JobStatusValue } from '../lib/jobs/jobStatus';

/**
 * BackgroundJobLog statu cevabi — JobsEndpoints.GetStatus DTO'su ile birebir.
 * `resultJson` sadece kucuk JSON sonuclar icin (ResultContentType=application/json,
 * <=64KB) inline doner; daha buyuk sonuclar /jobs/{id}/download stream eder.
 */
export type JobStatusDto = {
  id: string;
  jobType: string;
  status: JobStatusValue;
  totalItems: number;
  processedItems: number;
  failedItems: number;
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  hasResult: boolean;
  resultFileName: string | null;
  resultSizeBytes: number | null;
  resultJson: BulkJobResultJson | null;
};

/**
 * Kullanici-tetikli bulk job'lar icin standart Result JSON sema.
 * BulkInvoiceOrdersJob, BulkConvertInvoicesToEFaturaJob aynisi. failure satirlari
 * jobType'a gore farkli alanlar tasiyabilir (orderId vs invoiceId), bunu union
 * cozer.
 */
export type BulkJobResultJson = {
  ok: number;
  failed: number;
  failures: BulkJobFailureRow[];
};

export type BulkJobFailureRow = {
  reason: string;
  /** i18n interpolation values of `reason`; absent on rows persisted before the job wrote them. */
  params?: Record<string, string | number | null>;
  // BulkInvoiceOrders -> orderId + orderNumber
  orderId?: string;
  orderNumber?: string;
  // BulkConvertInvoicesToEFatura -> invoiceId
  invoiceId?: string;
};

/**
 * Bir BackgroundJobLog'u poll'lar. Pending/Running surece her 2sn'de bir yeniler;
 * terminal statu (Completed/Failed/Cancelled) gelir gelmez polling durur.
 *
 * `enabled` false gecince hiç sorgu atilmaz — drawer kapaliyken bos yere yuk yapma.
 */
export function useJobStatus(
  tenantId: string | null | undefined,
  jobId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  const enabled = (options?.enabled ?? true) && !!tenantId && !!jobId;

  return useQuery<JobStatusDto>({
    queryKey: ['job-status', tenantId, jobId],
    queryFn: async () => {
      const r = await api.get(`/api/v1/tenants/${tenantId}/jobs/${jobId}`);
      return r.data as JobStatusDto;
    },
    enabled,
    // Terminal statu gelince polling dur — gereksiz request'le DB'yi yormamak icin.
    refetchInterval: query => {
      // A 4xx (no Jobs.Read, job gone) will not heal by asking again.
      if (isDeterministicClientError(query.state.error)) return false;
      const data = query.state.data;
      if (!data) return 2000;
      return isTerminalJobStatus(data.status) ? false : 2000;
    },
    // Pencere foku geçtigi her seferinde tekrar fetch — kullanici sekme degistirip donerse guncel statu.
    refetchOnWindowFocus: query => !isDeterministicClientError(query.state.error),
    staleTime: 0,
  });
}

/**
 * BackgroundJobLog list item — `GET /api/v1/tenants/{tid}/jobs?jobType=...&limit=...`
 * cevabıyla birebir. Hacimli alanlar (ResultData, Parameters) projeksiyonda yer almaz;
 * detay icin `useJobStatus(id)` ile satira tiklanan job'i ayrica al.
 */
export type RecentJobItem = {
  id: string;
  jobType: string;
  status: JobStatusValue;
  totalItems: number;
  processedItems: number;
  failedItems: number;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  hasResultJson: boolean;
};

/**
 * Tenant'in son N bulk job'unu listeler. `jobType` filtresi opsiyonel — null/empty
 * verilirse tum job'lar gelir. Dropdown'da sadece BulkInvoiceOrders / ExportOrders gibi
 * tek tip listelemek icin filtre gecmek tercih edilir.
 *
 * `enabled` false iken sorgu atilmaz; dropdown kapali oldugunda backend bos yere
 * sorgulanmasin diye consumer kontrol eder.
 */
export function useRecentBulkJobs(
  tenantId: string,
  jobType: string | null,
  options?: { enabled?: boolean; limit?: number },
) {
  const enabled = (options?.enabled ?? true) && !!tenantId;
  const limit = options?.limit ?? 10;

  return useQuery<RecentJobItem[]>({
    queryKey: ['recent-jobs', tenantId, jobType ?? '__all__', limit],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      if (jobType) params.set('jobType', jobType);
      params.set('limit', String(limit));
      const r = await api.get(`/api/v1/tenants/${tenantId}/jobs?${params.toString()}`, { signal });
      return r.data as RecentJobItem[];
    },
    enabled,
    // Dropdown acildiktan sonra 30sn'de bir refresh — kullanici aciksa Pending/Running
    // job'lar guncellensin. Kapaliyken refetch yok.
    refetchInterval: 30_000,
    staleTime: 5_000,
  });
}
